import React, { useEffect, useState } from 'react';
import { database } from '../firebaseConfig';
import { ref, onValue, set, push, update, remove } from 'firebase/database';
import TextMessagePanel from './TextMessagePanel';
import { ANCHORS, ROLES, APPLIES_TO, DEFAULT_TASK_LISTS, sortByTimeline } from '../data/taskListDefaults';
import { TEAM_TASK_LISTS } from '../data/teamTaskLists';
import { quickActionLabel } from '../config/quickActions';
import { quickActionPreview } from '../config/quickActionMessages';
import { syncListToOpenDeals } from '../services/transactionTasks';
import { roleLabel } from '../config/roles';

// Sample values so the email preview shows what a real email looks like. Real emails fill these from the deal.
const SAMPLE_MERGE = {
  address: '123 Sample Way, Estero, FL',
  seller_names: 'Jane Seller',
  seller_phone: '239-555-0100',
  seller_email: 'jane@example.com',
  agent_first_name: 'Kelly',
  agent_last_name: 'Olin',
  agent_phone: '239-488-5900',
  co_agent_line: '<br><strong>Co-agent:</strong> First Last, coagent@example.com, 239-555-0199',
  client_name: 'Client',
  client_first_name_1: 'Jane',
  client_first_name_2: 'John',
  client_greeting: 'Jane and John',
  tc_first_name: 'Tina',
  tc_last_name: '',
  photographer_name: 'Pat Photographer',
  photography_date: 'Tuesday, October 20, 2026',
  listing_price: '$649,000',
  agent_full_name: 'Kelly Olin',
  agent_email: 'kelly@example.com',
  notes_line: '<p><strong>Notes:</strong> Sample photography notes.</p>',
  recipient_first_name: 'Lee',
  anticipated_listing_date: 'Monday, November 2, 2026',
  listing_effective_date: 'Thursday, November 5, 2026',
  seller_contacts_block: '<p>Jane Seller, 239-555-0100, jane@example.com</p><p>John Seller, 239-555-0101, john@example.com</p>',
  agents_block: '<p>Kelly Olin, kelly@example.com, 239-488-5900</p><p>First Last, coagent@example.com, 239-555-0199</p>',
};
const previewMerge = (text) => String(text || '').replace(/\{\{(\w+)\}\}/g, (match, key) => (SAMPLE_MERGE[key] ?? match));

// Comparison text for a list: sorted keys, and nulls and empty arrays left out (the database drops them).
const cleanForCompare = (value) => JSON.stringify(value, (key, val) => {
  if (val === null || val === undefined) return undefined;
  if (Array.isArray(val) && val.length === 0) return undefined;
  if (val && typeof val === 'object' && !Array.isArray(val)) {
    return Object.keys(val).sort().reduce((out, k) => { out[k] = val[k]; return out; }, {});
  }
  return val;
});

// Every list the admin can load: the starter lists plus the team lists (seller, buyer, rental, new construction).
const ALL_STARTER_LISTS = { ...DEFAULT_TASK_LISTS, ...TEAM_TASK_LISTS };

// Older copies of the seller lists. The team seller lists replace them, so they stay out of the admin screen and are not reloaded.
// 'new-landlord' is the older copy of the landlord list. The team list 'landlord-rental-listing' is the one kept.
const HIDDEN_LIST_IDS = ['listing-out-for-signing', 'seller-under-contract', 'new-landlord'];

const emptyTask = { name: '', description: '', role: 'Agent', anchor: 'created', offsetDays: 0, appliesTo: 'All', type: 'Task', autoSend: false, recipients: [] };

const NOTIFY_RECIPIENTS = ['Admin (Me)', 'Agent', 'Co-Agent', 'TC', 'Marketing Manager', 'Local Assistant', 'Cooperating Agent', 'Cooperating TC', 'Mortgage Lender', 'Title Company', 'Photographer', 'Gift Partner', 'Client'];

const dueText = (t) => {
  const anchor = ANCHORS[t.anchor] || t.anchor;
  const n = Number(t.offsetDays) || 0;
  if (n === 0) return `Day of ${anchor}`;
  return `${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'} ${n < 0 ? 'before' : 'after'} ${anchor}`;
};

const label = { display: 'block', fontSize: 13, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 };
const input = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #d8c9a8', borderRadius: 6, fontSize: 14, background: '#fff' };
const btnGold = { backgroundColor: '#1a1a1a', color: '#D4AF37', border: 'none', padding: '8px 14px', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 14 };
const btnLight = { backgroundColor: '#fff', color: '#1a1a1a', border: '1px solid #d8c9a8', padding: '8px 14px', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 14 };

const NO_FILTERS = { role: 'All', appliesTo: 'All', type: 'All' };

// Phases group each task by where the transaction is when it happens.
const PHASES = [
  { key: 'before-live', label: 'Before the listing goes live', anchors: ['created', 'photographerDate', 'targetListDate'] },
  { key: 'live', label: 'Once the listing is live', anchors: ['mlsLiveDate', 'listingExpirationDate'] },
  { key: 'under-contract', label: 'Once the listing is under contract', anchors: ['underContractDate', 'earnestMoneyDueDate', 'dueDiligenceDeadline', 'secondEmdDate', 'financingDeadline', 'homeSaleContingencyDate', 'forecastedClosingDate', 'settlementDate', 'leaseStartDate', 'moveInDate', 'leaseEndDate'] },
];
const phaseOf = (t) => (PHASES.find((p) => p.anchors.includes(t.anchor)) || PHASES[2]).key;
const FILTER_FIELDS = [
  { key: 'role', label: 'Assigned to', options: ['All', ...ROLES] },
  { key: 'appliesTo', label: 'Applies to', options: APPLIES_TO },
  { key: 'type', label: 'Type', options: ['All', 'Task', 'Notification'] },
];

const looksLikeHtml = (s) => /<[a-z][\s\S]*>/i.test(s || '');

const escapeHtml = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const toEmailHtml = (s) => (looksLikeHtml(s) ? s : escapeHtml(s).replace(/\n/g, '<br>'));

const toPlainText = (s) => {
  if (!looksLikeHtml(s)) return s || '';
  return s
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li[^>]*>/gi, '- ')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

// Groups run in order from the start of a deal to the end.
const LIST_GROUPS = [
  { label: 'Seller Task List', ids: ['seller-listing', 'seller-under-contract-team'] },
  { label: 'Buyer Task List', ids: ['resale-buyer-under-contract', 'new-construction-buyer-under-contract'] },
  { label: 'Rental Task List', ids: ['landlord-rental-listing', 'new-tenant-our-listing', 'new-tenant-not-our-listing', 'tenant-rental'] },
  { label: 'After Closing', ids: ['post-closing-follow-up'] },
];

export default function TaskListsSection() {
  const [lists, setLists] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(null); // { id?, ...task }
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(NO_FILTERS);
  const [viewing, setViewing] = useState(null); // notification task being viewed
  const [tab, setTab] = useState('preview');
  const [draft, setDraft] = useState({ subject: '', body: '' });
  const matchesFilters = (t) =>
    (filters.role === 'All' || t.role === filters.role) &&
    (filters.appliesTo === 'All' || t.appliesTo === filters.appliesTo) &&
    (filters.type === 'All' || t.type === filters.type);

  useEffect(() => {
    const unsub = onValue(ref(database, 'taskLists'), (snap) => {
      setLists(snap.val() || {});
      setLoading(false);
    }, (err) => {
      console.error('Could not load task lists:', err);
      setError('Could not load task lists.');
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Team lists follow the code: when a saved team list differs from data/teamTaskLists.js, the code version is saved.
  // Only the fields that matter are compared, so empty and blank values don't cause repeat saves.
  const syncedSignatures = React.useRef({});
  useEffect(() => {
    if (loading) return;
    Object.entries(TEAM_TASK_LISTS).forEach(([id, def]) => {
      if (HIDDEN_LIST_IDS.includes(id)) return;
      const codeTasks = {};
      sortByTimeline(def.tasks).forEach((t, i) => {
        codeTasks[`t${String(i + 1).padStart(3, '0')}`] = { ...t, order: i + 1 };
      });
      const target = { name: def.name, tasks: codeTasks };
      const signature = cleanForCompare(target);
      if (syncedSignatures.current[id] === signature) return;
      const saved = lists[id];
      if (saved && cleanForCompare({ name: saved.name, tasks: saved.tasks || {} }) === signature) {
        syncedSignatures.current[id] = signature;
        return;
      }
      syncedSignatures.current[id] = signature;
      set(ref(database, `taskLists/${id}`), target).catch((err) => console.error(`Could not sync ${id} to code`, err));
    });
  }, [lists, loading]);

  const listIds = Object.keys(lists).filter((id) => !HIDDEN_LIST_IDS.includes(id));
  const currentId = listIds.includes(selectedId) ? selectedId : listIds[0];
  const current = currentId ? lists[currentId] : null;
  const tasks = current?.tasks
    ? sortByTimeline(Object.entries(current.tasks).map(([id, t]) => ({ id, ...t })).sort((a, b) => (a.order || 0) - (b.order || 0)))
    : [];

  const visible = tasks.filter(matchesFilters);

  const openTemplate = (t) => {
    const starter = Object.values(DEFAULT_TASK_LISTS).flatMap((l) => l.tasks || []).find((d) => d.name === t.name && d.subject);
    const subject = t.subject || starter?.subject || '';
    const body = looksLikeHtml(t.body) ? t.body : (starter?.body || t.body || '');
    const smsBody = t.smsBody || starter?.smsBody || '';
    setViewing({ ...t, subject, body, smsBody });
    setDraft({ subject, body });
    setTab('preview');
  };

  const closeTemplate = () => {
    setViewing(null);
  };

  const saveTemplate = async () => {
    if (!viewing) return;
    setError('');
    try {
      await update(ref(database, `taskLists/${currentId}/tasks/${viewing.id}`), {
        subject: draft.subject.trim(),
        body: draft.body,
      });
      flash('Notification template saved.');
      closeTemplate();
    } catch (err) {
      console.error(err);
      setError('Could not save the notification template.');
    }
  };

  const resortCurrent = async () => {
    if (!current || !tasks.length) return;
    setError('');
    try {
      const sorted = sortByTimeline(tasks);
      await Promise.all(sorted.map((t, i) => update(ref(database, `taskLists/${currentId}/tasks/${t.id}`), { order: i + 1 })));
      flash('Tasks sorted from start to finish.');
    } catch (err) {
      console.error(err);
      setError('Could not sort the tasks.');
    }
  };

  const resortAll = async () => {
    setError('');
    try {
      const writes = [];
      listIds.forEach((id) => {
        const list = lists[id];
        const arr = list?.tasks ? Object.entries(list.tasks).map(([tid, t]) => ({ id: tid, ...t })) : [];
        sortByTimeline(arr).forEach((t, i) => {
          writes.push(update(ref(database, `taskLists/${id}/tasks/${t.id}`), { order: i + 1 }));
        });
      });
      await Promise.all(writes);
      flash('All task lists sorted from start to finish.');
    } catch (err) {
      console.error(err);
      setError('Could not sort the task lists.');
    }
  };

  const refreshTemplates = async () => {
    setError('');
    try {
      const writes = [];
      let added = 0;
      let updated = 0;
      const orphaned = [];
      const keyOf = (name, role) => `${name}||${role}`;
      Object.entries(DEFAULT_TASK_LISTS).forEach(([listId, def]) => {
        if (!lists[listId]) return;
        const savedEntries = Object.entries(lists[listId].tasks || {});
        const starterKeys = new Set((def.tasks || []).map((d) => keyOf(d.name, d.role)));
        let nextOrder = savedEntries.length + 1;
        (def.tasks || []).forEach((starter) => {
          const match = savedEntries.find(([, saved]) => keyOf(saved.name, saved.role) === keyOf(starter.name, starter.role));
          if (match) {
            const fields = {
              type: starter.type || 'Task',
              role: starter.role,
              anchor: starter.anchor,
              offsetDays: Number(starter.offsetDays) || 0,
              appliesTo: starter.appliesTo || 'All',
              autoSend: starter.type === 'Notification' ? !!starter.autoSend : null,
              description: starter.description || '',
              recipients: starter.recipients || null,
              requiresAnchor: starter.requiresAnchor || null,
              requiresField: starter.requiresField || null,
              requiresValue: starter.requiresValue || null,
              subject: starter.subject || null,
              body: starter.body || null,
              smsBody: starter.smsBody || null,
            };
            writes.push(update(ref(database, `taskLists/${listId}/tasks/${match[0]}`), fields));
            updated += 1;
          } else {
            added += 1;
            const { order, ...rest } = starter;
            writes.push(push(ref(database, `taskLists/${listId}/tasks`), {
              ...rest,
              subject: rest.subject || null,
              body: rest.body || null,
              recipients: rest.recipients || null,
              requiresAnchor: rest.requiresAnchor || null,
              requiresField: rest.requiresField || null,
              requiresValue: rest.requiresValue || null,
              order: nextOrder,
            }));
            nextOrder += 1;
          }
        });
        savedEntries.forEach(([, saved]) => {
          if (!starterKeys.has(keyOf(saved.name, saved.role))) orphaned.push(`${lists[listId].name}: ${saved.name}`);
        });
      });
      await Promise.all(writes);
      const parts = [];
      if (updated) parts.push(`updated ${updated} task${updated === 1 ? '' : 's'}`);
      if (added) parts.push(`added ${added} missing task${added === 1 ? '' : 's'}`);
      let text = parts.length ? `Starter lists: ${parts.join(', ')}.` : 'Starter lists are already up to date.';
      if (orphaned.length) {
        text += ` ${orphaned.length} saved task${orphaned.length === 1 ? ' is' : 's are'} no longer in the starter lists, so delete ${orphaned.length === 1 ? 'it' : 'them'} by hand: ${orphaned.join('; ')}.`;
      }
      flash(text);
    } catch (err) {
      console.error(err);
      setError('Could not update the starter lists.');
    }
  };

  const flash = (text) => { setMsg(text); setTimeout(() => setMsg(''), 3000); };

  const missingDefaults = Object.keys(ALL_STARTER_LISTS).filter((id) => !lists[id] && !HIDDEN_LIST_IDS.includes(id));

  const loadDefaults = async () => {
    setError('');
    try {
      const payload = {};
      Object.entries(ALL_STARTER_LISTS).forEach(([id, list]) => {
        if (lists[id] || HIDDEN_LIST_IDS.includes(id)) return;
        payload[id] = { name: list.name, tasks: {} };
        sortByTimeline(list.tasks).forEach((t, i) => {
          payload[id].tasks[`t${String(i + 1).padStart(3, '0')}`] = { ...t, order: i + 1 };
        });
      });
      await Promise.all(Object.entries(payload).map(([id, val]) => set(ref(database, `taskLists/${id}`), val)));
      flash('Starter task lists loaded.');
    } catch (err) {
      console.error(err);
      setError('Could not load the starter lists.');
    }
  };

  // Team lists (seller, buyer, rental) are not covered by "Update templates", so this replaces one team list
  // with the code version. Any saved edits on that list are lost, so it asks first.
  const resetListToCode = async (listId) => {
    const def = TEAM_TASK_LISTS[listId];
    if (!def) return;
    if (!window.confirm(`Replace the saved "${def.name}" list with the code version? Saved edits on this list will be lost.`)) return;
    try {
      const tasks = {};
      sortByTimeline(def.tasks).forEach((t, i) => {
        tasks[`t${String(i + 1).padStart(3, '0')}`] = { ...t, order: i + 1 };
      });
      await set(ref(database, `taskLists/${listId}`), { name: def.name, tasks });
      flash(`"${def.name}" reset to the code version.`);
    } catch (err) {
      console.error(err);
      setError('Could not reset this list.');
    }
  };

  const startNew = () => {
    setForm({ ...emptyTask });
  };

  const startEdit = (t) => {
    const { id, order, ...rest } = t;
    setForm({ id, ...rest });
  };

  const saveTask = async () => {
    if (!form?.name?.trim()) { setError('Enter a task name.'); return; }
    setError('');
    const { id, ...data } = form;
    const clean = {
      ...data,
      name: data.name.trim(),
      description: (data.description || '').trim(),
      offsetDays: Number(data.offsetDays) || 0,
      autoSend: data.type === 'Notification' ? !!data.autoSend : null,
      recipients: data.type === 'Notification' ? (data.recipients || []) : null,
    };
    try {
      if (id) {
        const existing = lists[currentId].tasks[id];
        await update(ref(database, `taskLists/${currentId}/tasks/${id}`), { ...clean, order: existing?.order || 0 });
      } else {
        const count = tasks.length;
        await push(ref(database, `taskLists/${currentId}/tasks`), { ...clean, order: count + 1 });
        // Every new task goes to all open deals on this list, and future deals pick it up too.
        let note = '';
        try {
          const added = await syncListToOpenDeals(currentId);
          note = added ? ` Added to ${added} open deal task${added === 1 ? '' : 's'}.` : ' No open deals needed it.';
        } catch (syncErr) {
          console.error('Could not add the task to open deals', syncErr);
          note = ' Open deals could not be updated.';
        }
        setForm(null);
        flash('Task saved.' + note);
        return;
      }
      setForm(null);
      flash('Task saved.');
    } catch (err) {
      console.error(err);
      setError('Could not save the task.');
    }
  };

  const deleteTask = async (t) => {
    if (!window.confirm(`Delete "${t.name}" from this list?`)) return;
    try {
      await remove(ref(database, `taskLists/${currentId}/tasks/${t.id}`));
      flash('Task deleted.');
    } catch (err) {
      console.error(err);
      setError('Could not delete the task.');
    }
  };

  if (loading) return <div className="bg-white rounded-lg shadow-sm p-6"><p>Loading task lists...</p></div>;

  if (!listIds.length) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700 }}>Task Lists</h2>
        <p style={{ color: '#7f7f7f', fontSize: 14 }}>No task lists yet. Load the starter lists (new listing, seller under contract, and resale buyer under contract) to edit them here.</p>
        {error && <p style={{ color: '#b00020', fontSize: 14 }}>{error}</p>}
        <button type="button" onClick={loadDefaults} style={btnGold}>Load starter task lists</button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>Task Lists</h2>
      <p style={{ color: '#7f7f7f', fontSize: 14, margin: '0 0 16px' }}>Edit the tasks that new transactions get. Each task has a role, a due date tied to a transaction date, and an optional auto-send for notifications.</p>

      {msg && <p style={{ color: '#2e7d32', fontSize: 14 }}>{msg}</p>}
      {error && <p style={{ color: '#b00020', fontSize: 14 }}>{error}</p>}

      {missingDefaults.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <button type="button" onClick={loadDefaults} style={btnLight}>Load {missingDefaults.length} missing starter list{missingDefaults.length === 1 ? '' : 's'}</button>
        </div>
      )}

      {(() => {
        const grouped = LIST_GROUPS.flatMap((g) => g.ids);
        const groups = [...LIST_GROUPS, { label: 'Other', ids: listIds.filter((id) => !grouped.includes(id)) }];
        return groups.map((g) => {
          const ids = g.ids.filter((id) => lists[id]);
          if (!ids.length) return null;
          return (
            <div key={g.label} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#8C6D1F', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>{g.label}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {ids.map((id) => (
                  <button key={id} type="button" onClick={() => { setSelectedId(id); setForm(null); }}
                    style={{ ...(id === currentId ? btnGold : btnLight) }}>
                    {lists[id].name} ({Object.keys(lists[id].tasks || {}).length})
                  </button>
                ))}
              </div>
            </div>
          );
        });
      })()}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{current?.name}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={resortCurrent} style={btnLight}>Sort start to finish</button>
          <button type="button" onClick={resortAll} style={btnLight}>Sort all lists</button>
          <button type="button" onClick={refreshTemplates} style={btnLight}>Update templates from starter lists</button>
          {TEAM_TASK_LISTS[selectedId] && (
            <button type="button" onClick={() => resetListToCode(selectedId)} style={btnLight}>Reset this list to code version</button>
          )}
          <button type="button" onClick={startNew} style={btnGold}>+ Add task</button>
        </div>
      </div>

      {form && (
        <div style={{ border: '1px solid #d8c9a8', borderRadius: 8, padding: 16, marginBottom: 16, background: '#faf8f3' }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700 }}>{form.id ? 'Edit task' : 'New task'}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={label}>Task name</label>
              <input style={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={label}>Description (optional)</label>
              <input style={input} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            {quickActionPreview(form.name, form.type) && (
              <div style={{ gridColumn: '1 / -1', background: '#fff', border: '1px solid #d8c9a8', borderRadius: 6, padding: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#8C6D1F', marginBottom: 6 }}>{quickActionPreview(form.name, form.type).label} (read-only: this is the wording the agent sees)</div>
                {quickActionPreview(form.name, form.type).items.map((item) => (
                  <div key={item.label} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{item.label}</div>
                    <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, margin: '4px 0 0', color: '#333' }}>{item.text}</pre>
                  </div>
                ))}
              </div>
            )}
            <div>
              <label style={label}>Assigned to</label>
              <select style={input} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>Applies to</label>
              <select style={input} value={form.appliesTo} onChange={(e) => setForm({ ...form, appliesTo: e.target.value })}>
                {APPLIES_TO.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>Due date tied to</label>
              <select style={input} value={form.anchor} onChange={(e) => setForm({ ...form, anchor: e.target.value })}>
                {Object.entries(ANCHORS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>Days (negative = before, positive = after, 0 = day of)</label>
              <input style={input} type="number" value={form.offsetDays} onChange={(e) => setForm({ ...form, offsetDays: e.target.value })} />
            </div>
            <div>
              <label style={label}>Type</label>
              <select style={input} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="Task">Task</option>
                <option value="Notification">Notification</option>
              </select>
            </div>
            {form.type === 'Notification' && (
              <div>
                <label style={{ ...label, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={!!form.autoSend} onChange={(e) => setForm({ ...form, autoSend: e.target.checked })} />
                  Auto-send
                </label>
              </div>
            )}
            {form.type === 'Notification' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={label}>Send notification to</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginTop: 6 }}>
                  {NOTIFY_RECIPIENTS.map((r) => {
                    const checked = (form.recipients || []).includes(r);
                    return (
                      <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const current = form.recipients || [];
                            const next = e.target.checked ? [...current, r] : current.filter((x) => x !== r);
                            setForm({ ...form, recipients: next });
                          }}
                        />
                        {roleLabel(r)}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="button" onClick={saveTask} style={btnGold}>Save task</button>
            <button type="button" onClick={() => setForm(null)} style={btnLight}>Cancel</button>
          </div>
        </div>
      )}

      {viewing && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }} onClick={closeTemplate}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#fff', borderRadius: 10, padding: 24, width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}>
            <div style={{ fontSize: 12, color: '#8C6D1F', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Notification template</div>
            <h3 style={{ margin: '4px 0 16px', color: '#1a1a1a' }}>{viewing.name}</h3>

            <label style={label}>Subject</label>
            <input style={{ ...input, width: '100%', boxSizing: 'border-box' }} value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 14, marginBottom: 10 }}>
              {[['preview', 'Preview'], ['html', 'HTML code'], ['text', 'Plain text'], ['sms', 'Text message']].map(([key, text]) => (
                <button key={key} type="button" onClick={() => setTab(key)} style={tab === key ? btnGold : btnLight}>{text}</button>
              ))}
            </div>

            {tab === 'preview' && (
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={`<html><head><meta charset="utf-8"></head><body style="margin:0;padding:16px;font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.5;color:#222;background:#fff">${toEmailHtml(previewMerge(draft.body)) || '<p style="color:#888">No message yet.</p>'}</body></html>`}
                style={{ width: '100%', height: 420, border: '1px solid #e5dcc8', borderRadius: 6, background: '#fff', boxSizing: 'border-box' }}
              />
            )}

            {tab === 'html' && (
              <>
                <textarea
                  style={{ ...input, width: '100%', boxSizing: 'border-box', minHeight: 320, fontFamily: 'monospace', fontSize: 13 }}
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                />
                <div style={{ fontSize: 12, color: '#7f7f7f', marginTop: 6 }}>Edit the HTML directly. The plain text version is built from this.</div>
              </>
            )}

            {tab === 'sms' && (
              viewing.smsBody
                ? <TextMessagePanel defaultBody={viewing.smsBody} to={[]} />
                : <div style={{ fontSize: 13, color: '#7f7f7f' }}>No text message for this notification yet.</div>
            )}

            {tab === 'text' && (
              <div style={{ whiteSpace: 'pre-wrap', border: '1px solid #e5dcc8', borderRadius: 6, padding: 12, backgroundColor: '#faf7f0', fontSize: 14, lineHeight: 1.5 }}>
                {toPlainText(previewMerge(draft.body)) || 'No message yet.'}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={saveTemplate} style={btnGold}>Save template</button>
              <button type="button" onClick={closeTemplate} style={btnLight}>Close</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
        {FILTER_FIELDS.map((f) => (
          <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#7f7f7f' }}>
            {f.label}
            <select
              value={filters[f.key]}
              onChange={(e) => setFilters({ ...filters, [f.key]: e.target.value })}
              style={{ padding: '6px 8px', fontSize: 13, minWidth: 170 }}
            >
              {f.options.map((o) => (
                <option key={o} value={o}>{o === 'TC' ? 'TC (Tina)' : o}</option>
              ))}
            </select>
          </label>
        ))}
        <button type="button" onClick={() => setFilters(NO_FILTERS)} style={{ ...btnLight, padding: '6px 12px' }}>Clear filters</button>
        <span style={{ fontSize: 12, color: '#7f7f7f' }}>Showing {tasks.filter(matchesFilters).length} of {tasks.length}</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr>
              {['#', 'Task', 'Assigned to', 'Applies to', 'Due', 'Type', 'Auto-send', ''].map((h) => (
                <th key={h} style={{ textAlign: 'left', fontSize: 12, color: '#7f7f7f', padding: '8px 6px', borderBottom: '1px solid #d8c9a8' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PHASES.map((p) => {
              const rows = visible.map((t, i) => ({ t, i })).filter(({ t }) => phaseOf(t) === p.key);
              if (rows.length === 0) return null;
              return (
              <React.Fragment key={p.key}>
              <tr><td colSpan={8} style={{ padding: '14px 6px 6px', fontWeight: 700, color: '#8C6D1F', borderBottom: '2px solid #d8c9a8' }}>{p.label}</td></tr>
              {rows.map(({ t, i }) => (
              <tr key={t.id}>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0ebe0' }}>{i + 1}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0ebe0', minWidth: 220 }}>
                  <div style={{ fontWeight: 600 }}>{t.name}</div>
                  {quickActionLabel(t.name, t.type) && (
                    <span style={{ display: 'inline-block', marginTop: 4, fontSize: 11, fontWeight: 700, color: '#8C6D1F', background: '#F6EFD9', border: '1px solid #D8C9A8', borderRadius: 4, padding: '2px 6px' }}>{quickActionLabel(t.name, t.type)}</span>
                  )}
                  {t.description && <div style={{ fontSize: 12, color: '#7f7f7f' }}>{t.description}</div>}
                </td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0ebe0' }}>{roleLabel(t.role)}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0ebe0' }}>{t.appliesTo}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0ebe0', minWidth: 180 }}>{dueText(t)}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0ebe0' }}>{t.type}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0ebe0' }}>{t.type === 'Notification' ? (t.autoSend ? 'Yes' : 'No') : 'N/A'}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0ebe0', whiteSpace: 'nowrap' }}>
                  {t.type === 'Notification' && (
                    <button type="button" onClick={() => openTemplate(t)} style={{ ...btnLight, padding: '4px 10px', marginRight: 6 }}>View</button>
                  )}
                  <button type="button" onClick={() => startEdit(t)} style={{ ...btnLight, padding: '4px 10px', marginRight: 6 }}>Edit</button>
                  <button type="button" onClick={() => deleteTask(t)} style={{ ...btnLight, padding: '4px 10px', color: '#b00020' }}>Delete</button>
                </td>
              </tr>
              ))}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
